import React, { useEffect, useState, useRef } from "react"
import NoSubscriptionsCard from "./NoSubscriptionsCard"
import SubscriptionCard from "./SubscriptionCard"
import { getStoreAccount } from "aldoo-ra/Store/api"
import { usePromise } from "aldoo-ra/usePromise"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import PubSub from "pubsub-js"

const Subscriptions = () => {
  const { lt } = Localization()
  const promiseResult = usePromise(getStoreAccount, true)
  const { result: account, execute, reset, pending } = promiseResult

  const texts = TextBundle({ id: "aldoo-store" })
  const [translatedSubscriptions, setTranslatedSubscriptions] = useState([])

  // Store execute and reset in refs to avoid dependency issues
  const executeRef = useRef(execute)
  const resetRef = useRef(reset)
  executeRef.current = execute
  resetRef.current = reset

  // Separate useEffect for initial data loading
  useEffect(() => {
    if (!account && !pending) executeRef.current()
  }, [account, pending])

  // Separate useEffect for PubSub subscription (only run once)
  useEffect(() => {
    const refresh = () => {
      if (resetRef.current && typeof resetRef.current === 'function') {
        resetRef.current()
      }
      executeRef.current()
    }

    const token = PubSub.subscribe("account-update", refresh)

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  // Separate useEffect for translation when account data changes
  useEffect(() => {
    const translateSubscriptions = async () => {
      if (!account?.subscriptions) {
        setTranslatedSubscriptions([])
        return
      }


      try {
        // Filter out deleted subscriptions and disabled ones
        // Note: The backend should handle this filtering, but we add it here as a safeguard
        const activeSubscriptions = account.subscriptions.filter(subscription => {
          // Exclude deleted subscriptions
          if (subscription.deleted === true) return false
          
          // Exclude disabled subscriptions  
          if (subscription.enabled === false) return false
          
          // Additional safety: exclude subscriptions with canceled status that should be hidden
          if (subscription.status === 'canceled' && subscription.deleted !== false) return false
          
          return true
        })


        const translated = await Promise.all(
          activeSubscriptions.map(async (subscription) => {
            // Create a deep copy to avoid mutating the original data
            const translatedSub = JSON.parse(JSON.stringify(subscription))

            // Translate product name if it exists
            if (translatedSub.product?.name) {
              translatedSub.product.name = await lt(translatedSub.product.name)
            }

            // Translate product description if it exists
            if (translatedSub.product?.description) {
              translatedSub.product.description = await lt(
                translatedSub.product.description
              )
            }

            return translatedSub
          })
        )

        
        // Sort subscriptions by end_date/renewal date (latest renewal first, then by creation)
        const sortedTranslated = translated.sort((a, b) => {
          // Primary sort: by end_date (latest renewal date first)
          let timestampA, timestampB
          
          if (a.end_date) {
            timestampA = new Date(a.end_date).getTime()
          } else if (a.created) {
            timestampA = new Date(a.created).getTime()
          } else {
            // Fallback to ObjectId timestamp
            timestampA = parseInt(a._id.substring(0, 8), 16) * 1000
          }
          
          if (b.end_date) {
            timestampB = new Date(b.end_date).getTime()
          } else if (b.created) {
            timestampB = new Date(b.created).getTime()
          } else {
            // Fallback to ObjectId timestamp  
            timestampB = parseInt(b._id.substring(0, 8), 16) * 1000
          }
          
          return timestampB - timestampA // Latest renewal first
        })
        
        
        setTranslatedSubscriptions(sortedTranslated)
      } catch (error) {
        console.error("Error translating subscriptions:", error)
        // Fallback to original subscriptions if translation fails (also sorted and filtered)
        const activeSubscriptions = account.subscriptions.filter(subscription => {
          // Exclude deleted subscriptions
          if (subscription.deleted === true) return false
          
          // Exclude disabled subscriptions  
          if (subscription.enabled === false) return false
          
          return true
        })
        
        const sortedFallback = activeSubscriptions.sort((a, b) => {
          // Primary sort: by end_date (latest renewal date first)
          let timestampA, timestampB
          
          if (a.end_date) {
            timestampA = new Date(a.end_date).getTime()
          } else if (a.created) {
            timestampA = new Date(a.created).getTime()
          } else {
            // Fallback to ObjectId timestamp
            timestampA = parseInt(a._id.substring(0, 8), 16) * 1000
          }
          
          if (b.end_date) {
            timestampB = new Date(b.end_date).getTime()
          } else if (b.created) {
            timestampB = new Date(b.created).getTime()
          } else {
            // Fallback to ObjectId timestamp  
            timestampB = parseInt(b._id.substring(0, 8), 16) * 1000
          }
          
          return timestampB - timestampA // Latest renewal first
        })
        setTranslatedSubscriptions(sortedFallback)
      }
    }

    translateSubscriptions()
  }, [account]) // Removed lt from dependencies to prevent infinite loops

  // Show loading state while fetching data
  if (pending && !account) {
    return (
      <div className="w-full max-w-[554px] flex items-center justify-center py-20">
        <div className="text-text_secondary">Loading...</div>
      </div>
    )
  }

  // Show empty state only after data is loaded
  if (!translatedSubscriptions.length) {
    return <NoSubscriptionsCard />
  }

  return (
    <div className="w-full max-w-[554px] flex flex-col items-center xl:items-start gap-[30px] xl:gap-[20px] mx-auto">
      {translatedSubscriptions.map((subscription, idx) => (
        <SubscriptionCard
          key={`subscription-${idx}`}
          subscription={subscription}
        />
      ))}
    </div>
  )
}

export default Subscriptions
